import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { Topbar } from '@/components/layout/Topbar';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import {
  ChevronLeft, CheckCircle2, Clock, AlertTriangle, ChevronRight, Loader2,
  ExternalLink, BookOpen, MapPin, User as UserIcon, FileText, PlayCircle,
} from 'lucide-react';
import { clientApi } from '@/lib/adminApi';
import { formatApiError } from '@/lib/api';
import { toast } from 'sonner';
import { useApp } from '@/context/AppContext';
import { bizDateTime } from '@/lib/dates';

/**
 * Sprint A2 — Per-assignment detail with sign-off dialog.
 *
 * Permission rules (mirror backend `patch_stage`):
 *   - client_admin / client_hr  → full control
 *   - stage.responsible_user_id → can sign off own stage
 *   - evaluated (self)          → can flip to in_progress only
 */

const STATUS_TONE = {
  not_started: 'bg-muted/40 text-muted-foreground border-muted',
  in_progress: 'bg-warning/15 text-warning border-warning/30',
  completed: 'bg-success/15 text-success border-success/30',
  overdue: 'bg-destructive/15 text-destructive border-destructive/30',
};

const TRAINING_TYPE_ICONS = {
  interview: UserIcon,
  workshop: BookOpen,
  practical_training: PlayCircle,
  reading_assignment: FileText,
  external_training: ExternalLink,
  certification: CheckCircle2,
  examination: FileText,
};

const fmtDateTime = (iso, lang = 'es') => bizDateTime(iso, lang);

export default function ClientTalentStartDetail() {
  const { t, i18n } = useTranslation();
  const lng = i18n.language;
  const navigate = useNavigate();
  const { assignmentId } = useParams();
  const { user } = useApp();

  const [asg, setAsg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dialogStage, setDialogStage] = useState(null);
  const [dialogStatus, setDialogStatus] = useState('completed');
  const [dialogNotes, setDialogNotes] = useState('');
  const [dialogEvidence, setDialogEvidence] = useState('');
  const [evidenceVerified, setEvidenceVerified] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    if (!assignmentId) return;
    setLoading(true);
    try {
      const d = await clientApi.onboardingGetAssignment(assignmentId);
      setAsg(d);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setLoading(false); }
  }, [assignmentId]);
  useEffect(() => { load(); }, [load]);

  // UAT remediation FINDING 1/2 — TalentStart is operated exclusively by
  // Client Admin / HR. No stage actions for employees or department owners.
  const canWrite = ['client_admin', 'client_hr'].includes(user?.role);
  const canSignOff = () => canWrite;

  const openSignOff = (stage) => {
    setDialogStage(stage);
    setDialogStatus('completed');
    setDialogNotes(stage.notes || '');
    setDialogEvidence(stage.evidence_url || '');
    setEvidenceVerified(false);
  };

  const quickPatch = async (stage, status) => {
    setSaving(true);
    try {
      const d = await clientApi.onboardingPatchStage(asg.id, stage.idx, { status });
      setAsg(d);
      toast.success(t('talentstart.detail.stageUpdated', { defaultValue: 'Stage updated.' }));
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setSaving(false); }
  };

  const handleDialogSave = async () => {
    if (!dialogStage) return;
    if (!evidenceVerified) {
      toast.error(t('talentstart.signoff.confirmRequired', { defaultValue: 'Debes confirmar que RRHH recibió y verificó la evidencia del departamento responsable.' }));
      return;
    }
    setSaving(true);
    try {
      const d = await clientApi.onboardingPatchStage(asg.id, dialogStage.idx, {
        status: dialogStatus,
        notes: dialogNotes,
        evidence_url: dialogEvidence,
        evidence_verified: true,
      });
      setAsg(d);
      setDialogStage(null);
      toast.success(t('talentstart.detail.stageUpdated', { defaultValue: 'Stage updated.' }));
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setSaving(false); }
  };

  if (loading || !asg) {
    return (
      <>
        <Topbar title={t('talentstart.detail.title', { defaultValue: 'Onboarding detail' })} />
        <div className="py-16 flex items-center justify-center text-muted-foreground" data-testid="talentstart-detail-loading">
          <Loader2 className="h-5 w-5 animate-spin mr-2" /> {t('common.loading', { defaultValue: 'Loading…' })}
        </div>
      </>
    );
  }

  return (
    <>
      <Topbar
        title={`${asg.user_name} · ${asg.user_position || asg.position_snapshot}`}
        subtitle={asg.name_snapshot}
        actions={
          <Button variant="outline" size="sm" onClick={() => navigate('/client/talentstart')}
                  data-testid="talentstart-detail-back">
            <ChevronLeft className="h-4 w-4 mr-1" />
            {t('talentstart.detail.back', { defaultValue: 'Back to TalentStart' })}
          </Button>
        }
      />

      <div className="p-6 lg:p-8 space-y-5" data-testid="client-talentstart-detail">
        {/* Header summary */}
        <Card className="p-5 shadow-card">
          <div className="grid sm:grid-cols-4 gap-4">
            <div>
              <p className="text-xs uppercase text-muted-foreground">{t('talentstart.detail.status', { defaultValue: 'Status' })}</p>
              <Badge className={STATUS_TONE[asg.status]} data-testid="talentstart-detail-status">
                {t(`talentstart.status.${asg.status}`, { defaultValue: asg.status })}
              </Badge>
            </div>
            <div>
              <p className="text-xs uppercase text-muted-foreground">{t('talentstart.detail.progress', { defaultValue: 'Progress' })}</p>
              <p className="text-xl font-bold tabular-nums" data-testid="talentstart-detail-progress">{asg.progress_pct}%</p>
            </div>
            <div>
              <p className="text-xs uppercase text-muted-foreground">{t('talentstart.detail.hours', { defaultValue: 'Hours (done / est.)' })}</p>
              <p className="text-xl font-bold tabular-nums" data-testid="talentstart-detail-hours">
                {asg.hours?.completed ?? 0} / {asg.hours?.estimated ?? 0}
              </p>
            </div>
            <div>
              <p className="text-xs uppercase text-muted-foreground">{t('talentstart.detail.due', { defaultValue: 'Due date' })}</p>
              <p className="text-sm font-medium" data-testid="talentstart-detail-due">{fmtDateTime(asg.due_at, lng)}</p>
            </div>
          </div>
        </Card>

        {/* Stages */}
        <Card className="p-5 shadow-card" data-testid="talentstart-detail-stages-card">
          <h3 className="font-semibold text-base mb-4">
            {t('talentstart.detail.stages', { defaultValue: 'Route stages' })}
          </h3>
          <ol className="space-y-3" data-testid="talentstart-detail-stages-list">
            {asg.stages.map((s) => {
              const Icon = TRAINING_TYPE_ICONS[s.training_type] || UserIcon;
              const isCurrent = s.idx === asg.current_stage_idx;
              return (
                <li key={s.idx} className={`rounded-lg border p-4 ${isCurrent ? 'border-primary bg-primary/5' : ''} ${s.status === 'completed' ? 'bg-success/5' : ''}`}
                    data-testid={`talentstart-detail-stage-${s.idx}`}>
                  <div className="flex items-start gap-3">
                    <div className="flex items-center justify-center h-10 w-10 rounded-full bg-muted shrink-0">
                      <span className="text-sm font-bold tabular-nums">{s.idx + 1}</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-semibold">{s.name}</p>
                        <Badge variant="outline" className={`text-xs ${STATUS_TONE[s.status]}`}>
                          {t(`talentstart.status.${s.status}`, { defaultValue: s.status })}
                        </Badge>
                        {s.is_external && (
                          <Badge variant="outline" className="text-xs bg-info/10 text-info border-info/30">
                            <ExternalLink className="h-2.5 w-2.5 mr-1" /> {t('talentstart.detail.external', { defaultValue: 'External' })}
                          </Badge>
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">
                        <Icon className="inline h-3 w-3 mr-1" />
                        {t(`talentstart.trainingType.${s.training_type}`, { defaultValue: s.training_type.replace('_', ' ') })}
                        {' · '}
                        {s.department_name && (<><MapPin className="inline h-3 w-3 mr-1" />{s.department_name}{' · '}</>)}
                        <Clock className="inline h-3 w-3 mr-1" /> {s.estimated_hours}h
                        {s.responsible_name && (<>{' · '}{s.responsible_name}</>)}
                      </p>
                      {s.description && <p className="text-sm mt-2">{s.description}</p>}
                      {s.notes && (
                        <p className="text-xs text-muted-foreground mt-2 italic">&ldquo;{s.notes}&rdquo;</p>
                      )}
                      {s.evidence_url && (
                        <a
                          href={s.evidence_url} target="_blank" rel="noreferrer"
                          className="inline-flex items-center gap-1 text-xs text-primary hover:underline mt-1"
                          data-testid={`talentstart-detail-stage-${s.idx}-evidence-link`}
                        >
                          <ExternalLink className="h-3 w-3" /> {t('talentstart.detail.evidence', { defaultValue: 'Evidence' })}
                        </a>
                      )}
                      {s.started_at && s.started_by_name && (
                        <p className="text-xs text-muted-foreground mt-1.5" data-testid={`talentstart-detail-stage-${s.idx}-started-by`}>
                          {t('talentstart.detail.startedBy', {
                            user: s.started_by_name,
                            date: fmtDateTime(s.started_at, lng),
                            defaultValue: 'Etapa iniciada por {{user}} el {{date}}',
                          })}
                        </p>
                      )}
                      {s.signed_off_by_name && s.completed_at && (
                        <p className="text-xs text-muted-foreground mt-0.5" data-testid={`talentstart-detail-stage-${s.idx}-signed-by`}>
                          {t('talentstart.detail.signedBy', {
                            user: s.signed_off_by_name,
                            date: fmtDateTime(s.completed_at, lng),
                            defaultValue: 'Firmada por {{user}} el {{date}} — evidencia externa verificada por RRHH',
                          })}
                        </p>
                      )}
                      {s.completed_at && !s.signed_off_by_name && (
                        <p className="text-xs text-muted-foreground mt-1">
                          ✓ {fmtDateTime(s.completed_at, lng)}
                        </p>
                      )}
                    </div>
                    {canWrite && (
                    <div className="flex flex-col gap-1 shrink-0">
                      {s.status !== 'completed' && s.status !== 'in_progress' && (
                        <Button size="sm" variant="outline" disabled={saving}
                                onClick={() => quickPatch(s, 'in_progress')}
                                data-testid={`talentstart-detail-stage-${s.idx}-start`}>
                          {t('talentstart.detail.start', { defaultValue: 'Iniciar' })}
                        </Button>
                      )}
                      {s.status === 'in_progress' && (
                        <Button size="sm" disabled={saving}
                                onClick={() => openSignOff(s)}
                                data-testid={`talentstart-detail-stage-${s.idx}-signoff`}>
                          <CheckCircle2 className="h-3.5 w-3.5 mr-1" />
                          {t('talentstart.detail.signOff', { defaultValue: 'Firmar' })}
                        </Button>
                      )}
                      {s.status === 'completed' && (
                        <Button size="sm" variant="outline" disabled={saving}
                                onClick={() => quickPatch(s, 'not_started')}
                                data-testid={`talentstart-detail-stage-${s.idx}-reopen`}>
                          {t('talentstart.detail.reopen', { defaultValue: 'Reabrir' })}
                        </Button>
                      )}
                    </div>
                    )}
                  </div>
                </li>
              );
            })}
          </ol>
        </Card>
      </div>

      {/* Sign-off dialog */}
      <Dialog open={!!dialogStage} onOpenChange={(o) => !o && setDialogStage(null)}>
        <DialogContent className="max-w-md" data-testid="talentstart-signoff-dialog">
          <DialogHeader>
            <DialogTitle>
              {t('talentstart.signoff.title', { defaultValue: 'Sign off stage' })}
              {dialogStage && <span className="block text-sm font-normal text-muted-foreground">{dialogStage.name}</span>}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="rounded-md border border-primary/30 bg-primary/5 p-3 flex items-start gap-2">
              <input
                type="checkbox"
                id="ts-evidence-verified"
                checked={evidenceVerified}
                onChange={(e) => setEvidenceVerified(e.target.checked)}
                className="mt-0.5 h-4 w-4 accent-primary"
                data-testid="talentstart-signoff-evidence-verified"
              />
              <label htmlFor="ts-evidence-verified" className="text-xs leading-relaxed cursor-pointer">
                {t('talentstart.signoff.evidenceVerifiedLabel', {
                  defaultValue: 'Confirmo que Recursos Humanos recibió y verificó la evidencia de respaldo del departamento responsable.',
                })}
              </label>
            </div>
            <div>
              <Label>{t('talentstart.signoff.evidence', { defaultValue: 'URL de evidencia externa (opcional)' })}</Label>
              <Input
                value={dialogEvidence}
                onChange={(e) => setDialogEvidence(e.target.value)}
                placeholder="https://…"
                data-testid="talentstart-signoff-evidence"
              />
              <p className="text-xs text-muted-foreground mt-1">
                {t('talentstart.signoff.evidenceHint', {
                  defaultValue: 'La evidencia se conserva en el repositorio documental de la organización. CTH no almacena archivos de onboarding.',
                })}
              </p>
            </div>
            <div>
              <Label>{t('talentstart.signoff.notes', { defaultValue: 'Notas (opcional)' })}</Label>
              <Textarea
                value={dialogNotes}
                onChange={(e) => setDialogNotes(e.target.value)}
                rows={3}
                data-testid="talentstart-signoff-notes"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogStage(null)} disabled={saving}>
              {t('common.cancel', { defaultValue: 'Cancel' })}
            </Button>
            <Button onClick={handleDialogSave} disabled={saving || !evidenceVerified} data-testid="talentstart-signoff-save">
              {saving && <Loader2 className="h-4 w-4 animate-spin mr-1" />}
              <CheckCircle2 className="h-4 w-4 mr-1" />
              {t('talentstart.signoff.confirm', { defaultValue: 'Mark completed' })}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
